import type { ProductLabelLayout } from './productLabelTypes';
import type { LabelFieldKey } from './labelLayoutEngine';
import { normalizeLabelFieldOrder } from './productLabelTypes';

export type LabelFieldContext = {
  hasProductImage: boolean;
  extraImageCount: number;
  hasPrice: boolean;
  hasNetQty: boolean;
  hasCategory: boolean;
  hasDescription: boolean;
  hasBenefits: boolean;
  hasHs: boolean;
  hasOrigin: boolean;
  hasExpiry: boolean;
  hasPackaging: boolean;
  hasCompany: boolean;
  hasMarketing: boolean;
  hasLegal: boolean;
  hasFooter: boolean;
};

export type LabelFieldDef = {
  key: LabelFieldKey;
  label: string;
  showKey?: keyof ProductLabelLayout;
  /** Enables the field on the label (toggle and/or default content). */
  enablePatch?: (layout: ProductLabelLayout) => Partial<ProductLabelLayout>;
};

export const LABEL_FIELD_DEFS: LabelFieldDef[] = [
  { key: 'brand', label: 'Brand', showKey: 'showBrand' },
  { key: 'image', label: 'Product image', showKey: 'showImage' },
  { key: 'extraImage0', label: 'Extra image 1', enablePatch: ensureExtraImageSlot(0) },
  { key: 'extraImage1', label: 'Extra image 2', enablePatch: ensureExtraImageSlot(1) },
  { key: 'name', label: 'Product name', showKey: 'showName' },
  { key: 'price', label: 'Sale price', showKey: 'showPrice' },
  { key: 'netQty', label: 'Net quantity', showKey: 'showNetQuantity' },
  { key: 'category', label: 'Category', showKey: 'showCategory' },
  { key: 'description', label: 'Description', showKey: 'showDescription' },
  { key: 'benefits', label: 'Benefits', showKey: 'showBenefits' },
  { key: 'hsCode', label: 'HS code', showKey: 'showHsCode' },
  { key: 'origin', label: 'Country of origin', showKey: 'showCountryOfOrigin' },
  { key: 'expiry', label: 'Shelf life', showKey: 'showExpiry' },
  { key: 'packaging', label: 'Packaging details', showKey: 'showPackaging' },
  { key: 'company', label: 'Company block', showKey: 'showCompanyInfo' },
  { key: 'marketing', label: 'Marketing', showKey: 'showMarketing' },
  { key: 'barcode', label: 'Barcode', showKey: 'showBarcode' },
  { key: 'sku', label: 'SKU', showKey: 'showSku' },
  {
    key: 'legal',
    label: 'Legal / handling line',
    enablePatch: (layout) => ({
      companyLegalLine:
        layout.companyLegalLine?.trim() || 'Store refrigerated · Recycle packaging',
    }),
  },
  {
    key: 'footer',
    label: 'Footer text',
    enablePatch: (layout) => ({
      showCompanyInfo: true,
      footerText: layout.footerText?.trim() || layout.companyWebsite || 'www.lewouhmart.com',
    }),
  },
];

function ensureExtraImageSlot(index: number) {
  return (layout: ProductLabelLayout): Partial<ProductLabelLayout> => {
    const extraImages = [...(layout.extraImages || [])];
    while (extraImages.length <= index) {
      extraImages.push({
        id: `extra-${Date.now()}-${extraImages.length}`,
        url: '',
        label: `Image ${extraImages.length + 1}`,
      });
    }
    return { extraImages };
  };
}

export function labelFieldDef(key: LabelFieldKey): LabelFieldDef | undefined {
  return LABEL_FIELD_DEFS.find((d) => d.key === key);
}

export function normalizeFieldOrder(raw?: LabelFieldKey[] | null): LabelFieldKey[] {
  return normalizeLabelFieldOrder(raw).filter((key) =>
    LABEL_FIELD_DEFS.some((d) => d.key === key),
  );
}

export function getEnabledFieldOrder(layout: ProductLabelLayout): LabelFieldKey[] {
  return normalizeFieldOrder(layout.fieldOrder).filter((key) => isFieldEnabled(layout, key));
}

export function isFieldEnabled(layout: ProductLabelLayout, key: LabelFieldKey): boolean {
  const def = labelFieldDef(key);
  if (!def) return false;
  if (def.showKey) return Boolean(layout[def.showKey]);
  if (key === 'legal') return Boolean(layout.companyLegalLine?.trim());
  if (key === 'footer') return Boolean(layout.footerText?.trim());
  if (key === 'extraImage0' || key === 'extraImage1') {
    const idx = key === 'extraImage0' ? 0 : 1;
    return (layout.extraImages?.length ?? 0) > idx;
  }
  return false;
}

export function isFieldRenderable(
  layout: ProductLabelLayout,
  key: LabelFieldKey,
  ctx: LabelFieldContext,
): boolean {
  if (!isFieldEnabled(layout, key)) return false;
  switch (key) {
    case 'image':
      return ctx.hasProductImage;
    case 'extraImage0':
      return ctx.extraImageCount > 0 && Boolean(layout.extraImages?.[0]?.url);
    case 'extraImage1':
      return ctx.extraImageCount > 1 && Boolean(layout.extraImages?.[1]?.url);
    case 'price':
      return ctx.hasPrice;
    case 'netQty':
      return ctx.hasNetQty;
    case 'category':
      return ctx.hasCategory;
    case 'description':
      return ctx.hasDescription;
    case 'benefits':
      return ctx.hasBenefits;
    case 'hsCode':
      return ctx.hasHs;
    case 'origin':
      return ctx.hasOrigin;
    case 'expiry':
      return ctx.hasExpiry;
    case 'packaging':
      return ctx.hasPackaging;
    case 'company':
      return ctx.hasCompany;
    case 'marketing':
      return ctx.hasMarketing;
    case 'legal':
      return ctx.hasLegal;
    case 'footer':
      return ctx.hasFooter;
    default:
      return true;
  }
}

export function getRenderableFieldOrder(
  layout: ProductLabelLayout,
  ctx: LabelFieldContext,
): LabelFieldKey[] {
  const order = normalizeFieldOrder(layout.fieldOrder);
  return order.filter((key) => isFieldRenderable(layout, key, ctx));
}

export function getAvailableFieldsToAdd(
  layout: ProductLabelLayout,
): LabelFieldDef[] {
  return LABEL_FIELD_DEFS.filter((def) => !isFieldEnabled(layout, def.key));
}

export function enableLabelField(
  layout: ProductLabelLayout,
  key: LabelFieldKey,
): Partial<ProductLabelLayout> {
  const def = labelFieldDef(key);
  if (!def) return {};
  const patch: Partial<ProductLabelLayout> = { ...(def.enablePatch?.(layout) || {}) };
  if (def.showKey) {
    (patch as Record<string, boolean>)[def.showKey] = true;
  }
  const fieldOrder = normalizeFieldOrder(layout.fieldOrder);
  if (!fieldOrder.includes(key)) {
    patch.fieldOrder = [...fieldOrder, key];
  }
  patch.layoutTemplate = 'FIELD_ORDER';
  return patch;
}

export function disableLabelField(
  layout: ProductLabelLayout,
  key: LabelFieldKey,
): Partial<ProductLabelLayout> {
  const def = labelFieldDef(key);
  if (!def) return {};
  const patch: Partial<ProductLabelLayout> = {};
  if (def.showKey) {
    (patch as Record<string, boolean>)[def.showKey] = false;
  }
  if (key === 'legal') patch.companyLegalLine = '';
  if (key === 'footer') patch.footerText = '';
  if (key === 'extraImage0' || key === 'extraImage1') {
    const idx = key === 'extraImage0' ? 0 : 1;
    const extraImages = [...(layout.extraImages || [])];
    if (extraImages.length > idx) {
      extraImages.splice(idx, 1);
      patch.extraImages = extraImages;
    }
  }
  return patch;
}

export function reorderLabelFields(
  order: LabelFieldKey[],
  draggedKey: LabelFieldKey,
  targetKey: LabelFieldKey,
  position: 'before' | 'after',
): LabelFieldKey[] {
  const normalized = normalizeFieldOrder(order);
  const fromIdx = normalized.indexOf(draggedKey);
  const targetIdx = normalized.indexOf(targetKey);
  if (fromIdx < 0 || targetIdx < 0 || fromIdx === targetIdx) return normalized;
  const next = normalized.filter((k) => k !== draggedKey);
  const insertAt = position === 'before' ? targetIdx : targetIdx + 1;
  const adjustedInsert = fromIdx < targetIdx ? insertAt - 1 : insertAt;
  next.splice(Math.max(0, adjustedInsert), 0, draggedKey);
  return next;
}
